import { memo, useCallback } from "react";

import { Link, TableCell, TableRow } from "@mui/material";
import { useTranslation } from "react-i18next";

import type { SystemTypeEnum } from "../../api/generated/graphql";
import { SystemGroupRoleEnum } from "../../api/generated/graphql";
import { useIsLoggedInUserSuperadmin } from "../../hooks/useLoggedInUser";
import { routes } from "../routes";
import type { FavoriteButtonProps } from "../shared/FavoriteButton/FavoriteButton";
import FavoriteButton from "../shared/FavoriteButton/FavoriteButton";
import TemplateSystemChip from "../shared/TemplateSystem/TemplateSystemChip";

import type { OverviewSystem } from "./index";

export type DesktopSystemTableRowProps = {
  system: OverviewSystem;
  onSystemClicked: (displayName: string) => void;
  onToggleFavorite: FavoriteButtonProps["onClick"];
  getSystemType: (type: SystemTypeEnum | string | null | undefined) => string;
};

const DesktopSystemTableRow = ({
  system,
  onSystemClicked,
  onToggleFavorite,
  getSystemType,
}: DesktopSystemTableRowProps) => {
  const { t } = useTranslation(["common"]);
  const isSuperadmin = useIsLoggedInUserSuperadmin();

  const isRestricted = system.myRole === SystemGroupRoleEnum.NoAccess && !isSuperadmin;

  const handleClick = useCallback(() => {
    if (isRestricted) {
      onSystemClicked(system.displayName);
    }
  }, [system.displayName, isRestricted, onSystemClicked]);

  const systemLinkHrefValue = isRestricted ? "#" : routes.systems.show.with({ systemId: system.id });

  return (
    <TableRow key={system.id}>
      <TableCell align="center">
        {system.myRole !== SystemGroupRoleEnum.NoAccess && (
          <FavoriteButton
            enabled={Boolean(system.isFavorite)}
            onClick={onToggleFavorite}
            favoriteTitle={t("common:favorite_system")}
            unfavoriteTitle={t("common:unfavorite_system")}
            entityId={system.id}
          />
        )}
      </TableCell>
      <TableCell align="center">
        <Link href={systemLinkHrefValue} onClick={handleClick}>
          {system.displayName}
        </Link>
        {system.isTemplate && <TemplateSystemChip />}
      </TableCell>
      <TableCell align="center">{system.addresses?.[0]?.region ?? ""}</TableCell>
      <TableCell align="center">{getSystemType(system?.type)}</TableCell>
      <TableCell align="center">{system.regulatoryId}</TableCell>
    </TableRow>
  );
};

export default memo(DesktopSystemTableRow);
